
import React from 'react';
import { Typography, Button, Space, Tag, Divider, message } from 'antd';
import { CopyOutlined, ImportOutlined, CalendarOutlined, TagOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const PromptSidePanel = ({ prompt, isOpen, onClose, onCopyToCanvas, type = 'prompt' }) => {
    if (!prompt) return null;

    const handleCopyText = () => {
        navigator.clipboard.writeText(prompt.content);
        message.success('Copied to clipboard');
    };

    const handleUse = () => {
        if (onCopyToCanvas) {
            onCopyToCanvas(prompt.content, type);
            onClose();
            message.success('Added to canvas');
        }
    };

    return (
        <div style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            background: '#0d1117',
            borderLeft: '1px solid #30363d'
        }}>
            <div style={{
                padding: '16px 24px',
                borderBottom: '1px solid #30363d',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#161b22'
            }}>
                <span style={{ fontSize: 16, fontWeight: 600, color: '#e6edf3' }}>
                    {type === 'system' ? 'System Prompt Details' : 'Prompt Details'}
                </span>
                <Button
                    type="text"
                    onClick={onClose}
                    style={{ color: '#8b949e' }}
                    icon={<span style={{ fontSize: 20 }}>×</span>}
                />
            </div>

            <div style={{ padding: 24, display: 'flex', flexDirection: 'column', flex: 1, overflowY: 'auto' }}>

                {/* Header Info */}
                <div style={{ marginBottom: 24 }}>
                    <Title level={3} style={{ color: '#fff', margin: 0, marginBottom: 8 }}>{prompt.name}</Title>
                    {prompt.description && (
                        <Text style={{ color: '#8b949e', fontSize: 14, display: 'block' }}>
                            {prompt.description}
                        </Text>
                    )}
                </div>

                {/* Tags */}
                {prompt.tags && prompt.tags.length > 0 && (
                    <div style={{ marginBottom: 24 }}>
                        <Space wrap>
                            {(prompt.tags || []).map(tag => (
                                <Tag key={tag} icon={<TagOutlined />} color="blue" style={{ background: 'rgba(24, 144, 255, 0.1)', borderColor: '#164c7e', color: '#58a6ff' }}>
                                    {tag}
                                </Tag>
                            ))}
                        </Space>
                    </div>
                )}

                {/* Media Gallery */}
                {prompt.mediaUrls && prompt.mediaUrls.length > 0 && (
                    <div style={{ marginBottom: 24 }}>
                        <div style={{
                            color: '#c9d1d9', fontWeight: 600, fontSize: 12,
                            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8
                        }}>
                            Media
                        </div>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                            gap: 8
                        }}>
                            {prompt.mediaUrls.map((media, idx) => (
                                <div key={idx} style={{
                                    borderRadius: 6, overflow: 'hidden',
                                    border: '1px solid #30363d',
                                    background: '#161b22',
                                    aspectRatio: '1'
                                }}>
                                    {media.type?.startsWith('video/') ? (
                                        <video
                                            src={media.url}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            muted
                                            onMouseEnter={e => e.target.play()}
                                            onMouseLeave={e => { e.target.pause(); e.target.currentTime = 0; }}
                                        />
                                    ) : (
                                        <img
                                            src={media.url}
                                            alt={media.name || 'Prompt media'}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Content Viewer */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', marginBottom: 24 }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 8,
                        color: '#c9d1d9',
                        fontWeight: 600,
                        fontSize: 12,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        <span>Content</span>
                        <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={handleCopyText}
                            style={{ color: '#8b949e' }}
                        >
                            Copy
                        </Button>
                    </div>
                    <div style={{
                        flex: 1,
                        background: '#161b22',
                        border: '1px solid #30363d',
                        borderRadius: 8,
                        padding: 16,
                        overflowY: 'auto',
                        fontFamily: 'monospace',
                        color: '#e6edf3',
                        whiteSpace: 'pre-wrap',
                        fontSize: 13,
                        lineHeight: 1.6
                    }}>
                        {prompt.content}
                    </div>
                </div>

                {/* Meta Info */}
                <div style={{ marginBottom: 24, fontSize: 12, color: '#484f58', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CalendarOutlined />
                    <span>Created: {new Date(prompt.createdAt).toLocaleDateString()}</span>
                </div>

                {/* Actions */}
                <Button
                    type="primary"
                    block
                    size="large"
                    icon={<ImportOutlined />}
                    onClick={handleUse}
                    style={{
                        height: 48,
                        fontSize: 16,
                        fontWeight: 500,
                        borderRadius: 8
                    }}
                >
                    Add to Canvas
                </Button>

            </div>
        </div>
    );
};

export default PromptSidePanel;
